import { Link, useLocation } from "wouter";
import { useEffect, useState } from "react";
import { signOut } from "../lib/authActions";
import { trpc } from "../lib/trpc";
import {
  LayoutDashboard,
  Trophy,
  Users,
  Award,
  Bell,
  BookOpen,
  Target,
  Sparkles,
  MessageCircle,
  Drama,
  Wrench,
  Lock,
  CheckSquare,
  Activity,
  Shuffle,
  Flag,
  Menu,
  X,
  LogOut,
  Crown,
  Phone,
} from "lucide-react";

type NavItem = {
  href: string;
  label: string;
  icon: typeof LayoutDashboard;
  admin?: boolean;
  master?: boolean;
};

// Performance + social — "how am I doing / what's happening".
const NAV: NavItem[] = [
  { href: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { href: "/leaderboard", label: "Leaderboard", icon: Trophy },
  { href: "/feed", label: "Team Feed", icon: Activity },
  { href: "/competitions", label: "Competitions", icon: Award },
  { href: "/team", label: "Team", icon: Users },
  { href: "/announcements", label: "Announcements", icon: Bell },
];

// Skill-building + selling tools — "how do I get better / close the deal".
const LEARN: NavItem[] = [
  { href: "/playbook", label: "Playbook", icon: Sparkles },
  { href: "/training", label: "Training Library", icon: BookOpen },
  { href: "/coach", label: "Coach", icon: MessageCircle },
  { href: "/roleplay", label: "Practice", icon: Drama },
  { href: "/vision", label: "Vision", icon: Target },
];

const ADMIN: NavItem[] = [
  { href: "/manager", label: "Manager View", icon: Crown, admin: true },
  { href: "/admin", label: "Admin", icon: Wrench, admin: true },
  { href: "/admin/playbook", label: "Playbook Editor", icon: BookOpen, admin: true },
  { href: "/admin/booking-check", label: "Booking Check", icon: CheckSquare, admin: true },
  { href: "/admin/team-goals", label: "Team Goals", icon: Flag, admin: true },
  { href: "/admin/reassign-leads", label: "Reassign Leads", icon: Shuffle, admin: true },
  { href: "/admin/sync-health", label: "Sync Health", icon: Activity, admin: true },
  { href: "/admin/alerts", label: "Alerts", icon: Bell, admin: true },
];

const MASTER: NavItem[] = [{ href: "/master-admin", label: "Master Admin", icon: Lock, master: true }];

export default function Sidebar({ isAdmin, isMaster }: { isAdmin: boolean; isMaster: boolean }) {
  const [location] = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Unread counts powering the small numbered badges. Refetched every 60s
  // so a new coach note / competition / announcement shows up reasonably
  // quickly without a page reload. Returns null when not authenticated.
  const { data: unread } = trpc.notifications.unread.useQuery(undefined, {
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  // Map a NavItem href to its unread count (0 = no badge).
  const badgeFor = (href: string): number => {
    if (!unread) return 0;
    if (href === "/coach") return unread.ai_coach ?? 0;
    if (href === "/announcements") return unread.announcements ?? 0;
    if (href === "/competitions") return unread.competitions ?? 0;
    if (href === "/training") return unread.training ?? 0;
    return 0;
  };

  // When the user navigates to a tracked page, clear that section's badge
  // (mark seen). Centralized here so individual pages don't need plumbing.
  const utils = trpc.useUtils();
  const markSeen = trpc.notifications.markSeen.useMutation({
    onSuccess: () => utils.notifications.unread.invalidate(),
  });
  useEffect(() => {
    const map: Record<string, "ai_coach" | "announcements" | "competitions"> = {
      "/coach": "ai_coach",
      "/announcements": "announcements",
      "/competitions": "competitions",
    };
    const section = map[location];
    if (section) markSeen.mutate({ section });
    // markSeen is stable from trpc — exclude from deps to avoid loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  // Close mobile drawer whenever the route changes
  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  // Lock body scroll when the drawer is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const renderItems = (items: NavItem[]) =>
    items.map(({ href, label, icon: Icon }) => {
      const active = location === href || location.startsWith(href + "/");
      const count = badgeFor(href);
      return (
        <Link
          key={href}
          href={href}
          className={
            "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition " +
            (active ? "bg-amber-500/10 text-amber-300" : "text-zinc-300 hover:bg-white/5")
          }
        >
          <Icon size={16} />
          <span className="flex-1">{label}</span>
          {count > 0 && (
            <span
              className="ml-1 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-amber-500/90 px-1.5 text-[10px] font-bold leading-5 text-ink-900"
              aria-label={`${count} new`}
            >
              {count > 99 ? "99+" : count}
            </span>
          )}
        </Link>
      );
    });

  const Brand = () => (
    <Link href="/dashboard" className="flex items-center gap-2 px-5 py-5 hover:bg-white/5">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 text-ink-900">
        <Trophy size={18} />
      </div>
      <div className="leading-tight">
        <div className="text-sm font-bold tracking-tight">The Solar Pro</div>
        <div className="text-xs text-zinc-400">Command Center</div>
      </div>
    </Link>
  );

  const NavList = () => (
    <nav className="flex-1 space-y-1 overflow-y-auto px-3 pb-4">
      <Link
        href="/setter-training"
        className={
          "mb-3 mt-2 flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-bold shadow-lg transition " +
          (location === "/setter-training"
            ? "bg-amber-300 text-ink-900"
            : "bg-gradient-to-r from-amber-400 to-amber-600 text-ink-900 hover:brightness-105")
        }
      >
        <Phone size={16} />
        <span className="flex-1">Setter Training</span>
      </Link>
      <div className="mt-2 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Main</div>
      {renderItems(NAV)}
      <div className="mt-4 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Learn &amp; Sell</div>
      {renderItems(LEARN)}
      {isAdmin && (
        <>
          <div className="mt-4 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Admin</div>
          {renderItems(ADMIN)}
        </>
      )}
      {isMaster && (
        <>
          <div className="mt-4 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Master</div>
          {renderItems(MASTER)}
        </>
      )}
    </nav>
  );

  const Footer = () => (
    <div className="px-5 py-4 text-xs text-zinc-500">
      <button
        onClick={() => signOut()}
        className="mb-3 flex w-full items-center gap-2 rounded-lg border border-white/10 px-3 py-2 text-sm font-medium text-zinc-300 hover:bg-white/5"
      >
        <LogOut size={15} /> Sign out
      </button>
      <div className="flex items-center gap-1.5">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 live-pulse" />
        GHL Sync: every 10 min
      </div>
      <div className="mt-1">v3.0 · Cloudflare</div>
    </div>
  );

  return (
    <>
      {/* Mobile top bar (visible < md). Sits above content; floats with backdrop-blur. */}
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-white/5 bg-ink-900/85 px-4 backdrop-blur md:hidden">
        <button
          aria-label="Open menu"
          onClick={() => setMobileOpen(true)}
          className="flex h-10 w-10 items-center justify-center rounded-lg border border-white/10 text-zinc-200 hover:bg-white/5 active:scale-95"
        >
          <Menu size={20} />
        </button>
        <Link href="/dashboard" className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 text-ink-900">
            <Trophy size={15} />
          </div>
          <div className="text-sm font-bold tracking-tight text-zinc-100">The Solar Pro</div>
        </Link>
        <Link
          href="/setter-training"
          className="rounded-full bg-amber-400 px-2.5 py-1 text-xs font-bold text-ink-900"
        >
          Setters
        </Link>
      </header>

      {/* Desktop sidebar (visible >= md) */}
      <aside className="hidden w-60 shrink-0 flex-col border-r border-white/5 bg-ink-800/40 backdrop-blur md:flex">
        <Brand />
        <NavList />
        <Footer />
      </aside>

      {/* Mobile drawer overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm md:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile drawer panel */}
      <aside
        className={
          "fixed inset-y-0 left-0 z-50 flex w-72 flex-col border-r border-white/5 bg-ink-800 shadow-2xl transition-transform duration-200 md:hidden " +
          (mobileOpen ? "translate-x-0" : "-translate-x-full")
        }
        aria-label="Mobile navigation"
      >
        <div className="flex items-center justify-between border-b border-white/5">
          <Brand />
          <button
            aria-label="Close menu"
            onClick={() => setMobileOpen(false)}
            className="mr-3 flex h-9 w-9 items-center justify-center rounded-lg text-zinc-300 hover:bg-white/5 active:scale-95"
          >
            <X size={20} />
          </button>
        </div>
        <NavList />
        <Footer />
      </aside>
    </>
  );
}
